import { useState, useEffect } from 'react'
import { Camera, Upload, Trash2, ArrowUp, ArrowDown, PersonStanding, Globe, Pencil, ArrowRight, RotateCcw } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { EventConfig } from '@/types'

interface PoseItem {
  filename: string
  enabled: boolean
}

export default function PosesPage({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { capture } = event

  const [globalPoses, setGlobalPoses] = useState<PoseItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [loadingGlobal, setLoadingGlobal] = useState(true)

  const poseSettings = capture.poseSuggestions || { enabled: true, showSecondsBefore: 2, mode: 'random', useGlobal: true, poses: [] }
  const enabled = poseSettings.enabled ?? true
  const showSecondsBefore = poseSettings.showSecondsBefore ?? 2
  const poseMode = poseSettings.mode || 'random'

  // useGlobal defaults to true if poses array is empty/absent (backwards compat)
  const useGlobal = poseSettings.useGlobal !== false && (!poseSettings.poses || poseSettings.poses.length === 0)
  const localPoses = poseSettings.poses || []

  useEffect(() => {
    setLoadingGlobal(true)
    fetch('/api/poses/settings')
      .then(r => r.json())
      .then(data => setGlobalPoses(data.poses || []))
      .catch(console.error)
      .finally(() => setLoadingGlobal(false))
  }, [])

  const updateSettings = (updates: {
    enabled?: boolean
    showSecondsBefore?: number
    mode?: 'random' | 'ordered'
    useGlobal?: boolean
    poses?: PoseItem[]
  }) => {
    onChange({
      capture: {
        ...capture,
        poseSuggestions: {
          enabled: updates.enabled !== undefined ? updates.enabled : enabled,
          showSecondsBefore: updates.showSecondsBefore !== undefined ? updates.showSecondsBefore : showSecondsBefore,
          mode: updates.mode !== undefined ? updates.mode : poseMode,
          useGlobal: updates.useGlobal !== undefined ? updates.useGlobal : useGlobal,
          poses: updates.poses !== undefined ? updates.poses : localPoses,
        }
      }
    })
  }

  // Switch to custom mode, pre-populate with currently-enabled global poses
  const handleSwitchToCustom = () => {
    const seed: PoseItem[] = globalPoses.filter(p => p.enabled).map(p => ({ ...p }))
    updateSettings({ useGlobal: false, poses: seed.length > 0 ? seed : globalPoses.map(p => ({ ...p })) })
  }

  // Reset back to global library
  const handleResetToGlobal = () => {
    if (!confirm('Reset to the Global Pose Library? Your custom pose selection for this event will be cleared.')) return
    updateSettings({ useGlobal: true, poses: [] })
  }

  const handleTogglePose = (filename: string) => {
    const updated = localPoses.map(p => p.filename === filename ? { ...p, enabled: !p.enabled } : p)
    updateSettings({ poses: updated })
  }

  const handleSelectAll = () => updateSettings({ poses: localPoses.map(p => ({ ...p, enabled: true })) })
  const handleDeselectAll = () => updateSettings({ poses: localPoses.map(p => ({ ...p, enabled: false })) })

  const handleMovePose = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === localPoses.length - 1) return
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const updated = [...localPoses]
    const temp = updated[index]
    updated[index] = updated[targetIndex]
    updated[targetIndex] = temp
    updateSettings({ poses: updated })
  }

  const handleDeletePose = async (filename: string) => {
    if (!confirm('Remove this pose from the event? (The file stays in the Global Library.)')) return
    const updated = localPoses.filter(p => p.filename !== filename)
    updateSettings({ poses: updated })
  }

  const handleUploadPose = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fetch('/api/poses/upload', { method: 'POST', body: formData })
      if (res.ok) {
        const data = await res.json()
        const newFilename = data.url.split('/').pop()
        const r = await fetch('/api/poses/settings')
        const settingsData = await r.json()
        setGlobalPoses(settingsData.poses || [])
        if (!localPoses.some(p => p.filename === newFilename)) {
          updateSettings({ poses: [...localPoses, { filename: newFilename, enabled: true }] })
        }
      }
    } catch (e) { console.error(e) }
    finally { setUploading(false); e.target.value = '' }
  }


  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <PersonStanding className="h-5 w-5 text-primary" />
          Pose Suggestions
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure which pose ideas are shown during the countdown. Use the global library or customise per event.
        </p>
      </div>

      {/* Enable / Disable */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-6">
        <label className="flex items-center justify-between p-4 bg-background border border-border rounded-xl cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="space-y-0.5">
            <span className="text-sm font-semibold text-foreground">Enable Pose Suggestions</span>
            <p className="text-xs text-muted-foreground">Show pose images to guests during photo capture countdowns</p>
          </div>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => updateSettings({ enabled: e.target.checked })}
            className="h-5 w-5 rounded border-border text-primary focus:ring-primary cursor-pointer bg-background"
          />
        </label>

        {enabled && (
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="font-semibold text-muted-foreground">Pose Intro Duration</span>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded text-foreground">{showSecondsBefore}s</span>
            </div>
            <input
              type="range" min={0} max={20} value={showSecondsBefore}
              onChange={(e) => updateSettings({ showSecondsBefore: Number(e.target.value) })}
              className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <p className="text-xs text-muted-foreground">Seconds before capture to show the pose suggestor.</p>
          </div>
        )}
      </div>

      {enabled && (
        <>
          {/* Source selector */}
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Option A: Global Library */}
            <button
              type="button"
              onClick={() => !useGlobal && handleResetToGlobal()}
              className={`flex flex-col items-start p-5 rounded-xl border text-left transition-all ${
                useGlobal
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-card hover:bg-muted/30 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Globe className={`h-4 w-4 ${useGlobal ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="font-semibold text-sm">Use Global Library</span>
                {useGlobal && <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">Active</span>}
              </div>
              <p className="text-xs text-muted-foreground">
                Automatically uses the shared pose library managed in{' '}
                <span className="font-semibold text-foreground">Admin → Poses</span>.
                Any changes to the library instantly apply here.
              </p>
            </button>

            {/* Option B: Custom */}
            <button
              type="button"
              onClick={() => useGlobal && handleSwitchToCustom()}
              className={`flex flex-col items-start p-5 rounded-xl border text-left transition-all ${
                !useGlobal
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border bg-card hover:bg-muted/30 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Pencil className={`h-4 w-4 ${!useGlobal ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className="font-semibold text-sm">Custom for this Event</span>
                {!useGlobal && <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded">Active</span>}
              </div>
              <p className="text-xs text-muted-foreground">
                Choose exactly which poses to use and in what order, independent of the global library.
              </p>
            </button>
          </div>

          {/* Global library preview */}
          {useGlobal && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold">Global Library Preview</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    These are the currently enabled poses from the global library. To change them, go to{' '}
                    <Link to="/admin/poses" className="text-primary hover:underline font-semibold">Admin → Poses</Link>.
                  </p>
                </div>
                <Link
                  to="/admin/poses"
                  className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors shrink-0"
                >
                  Manage Library <ArrowRight className="h-3 w-3" />
                </Link>
              </div>

              {loadingGlobal ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground gap-3">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                  Loading...
                </div>
              ) : globalPoses.filter(p => p.enabled).length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-12 text-muted-foreground bg-card/20">
                  <Globe className="h-8 w-8 opacity-30 mb-3" />
                  <p className="text-sm font-semibold">Global library is empty or all poses are disabled</p>
                  <Link to="/admin/poses" className="text-xs mt-1 text-primary hover:underline">Upload poses in Admin → Poses →</Link>
                </div>
              ) : (
                <div className="grid gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 opacity-80">
                  {globalPoses.filter(p => p.enabled).map((p) => (
                    <div key={p.filename} className="flex flex-col rounded-xl border border-border/60 p-2 bg-card/50">
                      <div className="aspect-[3/4] w-full rounded-lg bg-muted/40 overflow-hidden flex items-center justify-center p-1">
                        <img src={`/poses/${p.filename}`} className="h-full w-full object-contain drop-shadow" alt="" />
                      </div>
                      <p className="mt-2 text-[10px] font-medium truncate text-muted-foreground text-center" title={p.filename}>
                        {p.filename.replace(/\.[^/.]+$/, '')}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={handleSwitchToCustom}
                className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors mt-2"
              >
                <Pencil className="h-3 w-3" />
                Switch to custom poses for this event
              </button>
            </div>
          )}

          {/* Custom pose editor */}
          {!useGlobal && (
            <div className="space-y-4">
              {/* Mode selection */}
              <div className="rounded-xl border border-border bg-card/50 p-4 space-y-3">
                <span className="text-sm font-semibold text-foreground">Suggestion Order</span>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(['random', 'ordered'] as const).map((m) => (
                    <button
                      key={m} type="button"
                      onClick={() => updateSettings({ mode: m })}
                      className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all ${
                        poseMode === m
                          ? 'border-primary bg-primary/5 text-foreground ring-1 ring-primary'
                          : 'border-border bg-background text-muted-foreground hover:bg-muted/30'
                      }`}
                    >
                      <span className="font-semibold text-sm text-foreground">
                        {m === 'random' ? 'Randomised Order' : 'Specific Order'}
                      </span>
                      <span className="text-xs mt-0.5 text-muted-foreground">
                        {m === 'random' ? 'Randomly shuffles enabled poses each session.' : 'Shows poses in the exact order below.'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Pose grid */}
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
                <div>
                  <h3 className="text-base font-semibold">Event Pose Selection</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Toggle and order the poses for this event specifically.</p>
                  {localPoses.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={handleSelectAll} className="text-[11px] font-semibold text-primary hover:underline">Select All</button>
                      <span className="text-muted-foreground/30 text-[10px] select-none">|</span>
                      <button onClick={handleDeselectAll} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground">Deselect All</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleResetToGlobal}
                    className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset to Global
                  </button>
                  <label className={`cursor-pointer rounded-lg border border-border bg-secondary px-4 py-1.5 text-xs font-semibold hover:bg-secondary/80 transition-colors flex items-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Upload className="h-3.5 w-3.5" />
                    {uploading ? 'Uploading...' : 'Upload Pose'}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUploadPose} disabled={uploading} />
                  </label>
                </div>
              </div>

              {localPoses.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-muted-foreground bg-card/20">
                  <Camera className="h-8 w-8 opacity-40 mb-3" />
                  <p className="text-sm font-semibold">No custom poses added yet</p>
                  <p className="text-xs mt-0.5 opacity-60">Upload PNG files or switch back to the Global Library.</p>
                </div>
              ) : (
                <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {localPoses.map((p, idx) => (
                    <div
                      key={p.filename}
                      className={`group relative flex flex-col rounded-xl border transition-all p-3 bg-card ${
                        p.enabled ? 'border-border/80 hover:border-primary/50' : 'border-border/40 opacity-50 hover:opacity-85'
                      }`}
                    >
                      <div className="relative aspect-[3/4] w-full rounded-lg bg-muted/40 border border-border/20 overflow-hidden flex items-center justify-center p-2">
                        <img src={`/poses/${p.filename}`} className="h-full w-full object-contain drop-shadow" alt="" />
                        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => handleDeletePose(p.filename)}
                            className="rounded-md bg-destructive/10 p-1.5 text-destructive hover:bg-destructive/20 border border-destructive/20"
                            title="Remove from event"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={p.enabled}
                          onChange={() => handleTogglePose(p.filename)}
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer bg-background"
                          id={`evt-pose-chk-${p.filename}`}
                        />
                        <label
                          htmlFor={`evt-pose-chk-${p.filename}`}
                          className="text-xs font-medium truncate flex-1 cursor-pointer select-none text-foreground/90"
                          title={p.filename}
                        >
                          {p.filename.replace(/\.[^/.]+$/, '')}
                        </label>
                      </div>
                      {poseMode === 'ordered' && (
                        <div className="mt-2.5 pt-2.5 border-t border-border/40 flex justify-between gap-1">
                          <button
                            type="button" onClick={() => handleMovePose(idx, 'up')} disabled={idx === 0}
                            className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-secondary hover:bg-secondary/80 text-[10px] font-semibold disabled:opacity-30 disabled:pointer-events-none transition-colors"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            type="button" onClick={() => handleMovePose(idx, 'down')} disabled={idx === localPoses.length - 1}
                            className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-secondary hover:bg-secondary/80 text-[10px] font-semibold disabled:opacity-30 disabled:pointer-events-none transition-colors"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
